import {
  resolvePlanForTier,
  type InvoiceLine,
  type PricingRuleSet,
} from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { t } from '../../../lib/i18n';
import {
  FINANCE_CLIENTS_QUERY_KEY,
  carInvoicePreviewQueryKey,
  createInvoice,
  fetchCarInvoicePreview,
  financeErrorCode,
  financeErrorMessage,
} from '../api';
import { tierLabel } from '../labels';
import type { FinanceClient, Invoice, InvoiceKind } from '../types';
import {
  INVOICE_DUE_DAYS,
  buildInvoiceDraft,
  chfOrDash,
  cn,
  dueDate,
  dueDateLabel,
  formatChf,
  formatNumber,
  invoiceKindsFor,
  invoiceOwed,
  prepaymentPreview,
} from '../utils';
import { InvoiceLineCaption, InvoiceLinesReadOnly, refusalText } from './invoice-lines';

interface IssueInvoiceDrawerProps {
  onClose: () => void;
  /** Balances, so the drawer can pick a client and print what they already owe. */
  clients: FinanceClient[];
  /**
   * The tariff in force. Required, never optional and never defaulted: the
   * drawer has nothing to quote without it, and quoting the package defaults
   * would put an unpublished grid on a money demand. The page renders its error
   * state instead of mounting this.
   */
  rules: PricingRuleSet;
  /**
   * Opened from a «Угоди» row: the client and the DEAL are already known, so the
   * drawer starts on that client with the car kind, and the car invoice — which
   * the API prices server-side from the deal — becomes billable. Without it the
   * drawer behaves exactly as before (no endpoint lists a client's won lots).
   */
  /**
   * Opened from a «Угоди» row. `clientName` is that row's label: the finance
   * roll-up lists only clients with a ledger or invoice footprint, so a client
   * billed for the first time (a trusted one with no deposit) is absent from
   * `clients` and the select would otherwise show the raw user id.
   */
  prefill?: { userId: string; dealId: string; clientName?: string };
}

/** A pill in one of the drawer's chip rows. */
function Chip({
  active,
  onClick,
  children,
  mono,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-9 rounded-full border px-3.5 text-[13px] font-semibold',
        mono && 'px-3 font-mono text-[12px] font-normal',
        active ? 'border-cf-brand bg-cf-brand text-white' : 'border-cf-line bg-cf-surface text-cf-ink',
      )}
    >
      {children}
    </button>
  );
}

/** One titled panel inside the drawer body. */
function Step({
  label,
  children,
  gap = 'gap-2.5',
  trailing,
}: {
  label: string;
  children: React.ReactNode;
  gap?: string;
  trailing?: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-cf-lg border border-cf-line bg-cf-surface px-[18px] py-4',
        gap,
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
          {label}
        </p>
        {trailing}
      </div>
      {children}
    </section>
  );
}

/**
 * «Виставити рахунок» — `Redesign-D-Admin`, the drawer at the right edge.
 *
 * Four steps in the order an operator thinks: who, what for, how much, and
 * when.
 *
 * ── It POSTS for real now, and the API answers a REASON ─────────────────────
 * The submit button calls `POST /api/admin/finance/invoices`. While
 * `PAYMENTS_ENABLED` is off the API answers 409 with
 * `code: 'PAYMENTS_DISABLED'` and writes nothing at all — a deliberate,
 * typed refusal rather than a 500 ("we broke") or a 200 ("the client has been
 * invoiced"), because the screen has to be able to say neither. That code is
 * rendered as an EXPLANATION, not as a generic failure: the operator is told
 * issuing is switched off pending the legal ADR, which is a different sentence
 * from "something went wrong" and leads to a different action.
 *
 * The 401/403 fork matters here too. Those come from the guard ladder, carry a
 * STRING on `error`, and never reach the handler's envelope — `financeErrorCode`
 * therefore branches on the status before reading a body, and the page hoists
 * them to the shared access-denied screen rather than showing them in a drawer.
 *
 * ── ⚠️ THE CAR KIND IS BILLABLE ONLY WITH A `prefill` DEAL ──────────────────
 * Opened from a «Угоди» row (`prefill {userId, dealId}`) the car invoice is
 * issued for that deal and priced by the server. Opened from «Фінанси» the
 * rest of this section still holds:
 * The artboard's step 2 draws a picker of the client's WON LOTS, and there is
 * NO ENDPOINT that serves them. `InvoiceDraftClientSchema` and
 * `InvoiceLotSchema` exist in `@carsfinders/contracts`, but nothing in
 * `apps/api` produces either: the admin-finance surface has no draft-client
 * route, and `POST .../invoices` requires a `dealId` this screen has no way to
 * learn. Cross-wiring `GET /won-cars` to guess one would be papering over a
 * missing contract with a different domain's ids.
 *
 * So the chip stays, and selecting it says exactly that. Hiding it would make
 * the gap invisible — and an invisible gap never gets an endpoint. Everything
 * the drawer CAN do (subscription, surcharge, ad-hoc line) it does end to end.
 *
 * The «next invoice number» the artboard prints in the eyebrow is gone for the
 * same reason and a better one: the number is ALLOCATED BY THE SERVER at write
 * time. A number shown before the write is a guess, and two operators drafting
 * at once would both be shown the same one.
 *
 * ── A deal's car invoice is PREVIEWED, READ-ONLY (owner, 2026-09-28) ─────────
 * With a `prefill` deal, step 03 fetches `GET .../invoices/car-preview` — the
 * server's own line builder and `prepaymentFor`, nothing written — and renders
 * the lines READ-ONLY. Car-invoice lines are fixed and system-calculated:
 * nobody may override them, so the drawer posts only `{userId, kind: 'car',
 * dealId, dueAt}` and the server prices the lines itself. The Swiss platform
 * fee is not among them — CarsFinders pays it out of its commission. Only the
 * non-car kinds (subscription, surcharge, other) keep editable lines.
 *
 * ── The deposit is a PREPAYMENT, applied by the server (phase 6) ───────────
 * On a car invoice the API takes `prepaymentFor(deposit, total)` off the
 * client's deposit inside the SAME transaction that writes the invoice (owner
 * decision c: a larger deposit keeps its remainder). The drawer only PREVIEWS
 * that line with the same contract function; it sends no deposit field. The
 * artboard's «Зарахувати депозит» checkbox is therefore rendered checked and
 * disabled with the reason — the backend has no way to issue a car invoice
 * WITHOUT the prepayment, and a working toggle would promise one.
 *
 * ── No instalment split ─────────────────────────────────────────────────────
 * The artboard's «Двома частинами» + 30/50/70% chips are omitted: an invoice
 * stores one total and one `dueAt`, so a split would be a schedule the issued
 * document does not carry. Flagged for the owner (phase 10 report).
 */
export function IssueInvoiceDrawer({ onClose, clients, rules, prefill }: IssueInvoiceDrawerProps) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState(prefill?.userId ?? clients[0]?.userId ?? '');
  const [kind, setKind] = useState<InvoiceKind | null>(prefill ? 'car' : null);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [description, setDescription] = useState<string | null>(null);
  const [dueDays, setDueDays] = useState(7);
  const [issued, setIssued] = useState<Invoice | null>(null);

  // Escape closes the drawer — it is a modal layer, and a modal that only
  // closes by mouse strands anyone working from the keyboard.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A prefilled client is never swapped for clients[0]: the deal is theirs.
  const client = clients.find((c) => c.userId === userId) ?? (prefill ? undefined : clients[0]);
  const tier = client?.tier ?? 'retail';
  const plan = resolvePlanForTier(rules.plans, tier);
  const kinds = invoiceKindsFor(plan);
  const activeKind: InvoiceKind =
    kind && kinds.includes(kind) ? kind : kinds.includes('sub') ? 'sub' : 'extra';

  const descriptionValue =
    description ?? (activeKind === 'extra' ? t('adminFinance.drawer.defaultSurcharge') : '');

  const draft = useMemo(
    () =>
      buildInvoiceDraft(rules, {
        kind: activeKind,
        tier,
        // Always null: no endpoint supplies a won lot — see the docblock.
        lot: null,
        description: descriptionValue,
        overrides,
      }),
    [rules, activeKind, tier, descriptionValue, overrides],
  );

  const deposit = client?.depositChf ?? 0;
  const dueLabel = dueDateLabel(dueDays);
  const owedAmount = client?.outstandingChf ?? 0;
  const planText = t('adminFinance.drawer.planOne', { date: dueLabel });

  /** A car invoice for the prefilled deal: the server prices it, so no lines are sent. */
  const carFromDeal = activeKind === 'car' && Boolean(prefill?.dealId);

  /**
   * The server's calculation for the prefilled deal (owner, 2026-09-28): the
   * SAME line builder and `prepaymentFor` the issue runs, fetched before
   * anything is written, so the operator sees every line before issuing.
   */
  const previewQuery = useQuery({
    queryKey: carInvoicePreviewQueryKey(prefill?.dealId ?? '', userId),
    queryFn: () => fetchCarInvoicePreview(prefill?.dealId ?? '', userId),
    enabled: carFromDeal && Boolean(prefill?.dealId) && Boolean(userId),
    retry: false,
  });
  const carPreview = carFromDeal ? previewQuery.data : undefined;
  // Display only: the server recomputes the totals inside the issue transaction.
  const carTotals = carPreview ?? null;

  /**
   * The prepayment PREVIEW — only where the server will actually apply one: a
   * billable car invoice and a positive deposit. For a deal-priced invoice it
   * is the server's deposit against the total; until the preview
   * answers there is none, and «До сплати» is unknown (never a confident CHF 0).
   */
  const carBillable = activeKind === 'car' && (carFromDeal || draft.lines.length > 0);
  const prepayment = carFromDeal
    ? carTotals && carTotals.prepaymentChf > 0
      ? { prepaymentChf: carTotals.prepaymentChf, upTo: false, toPayChf: carTotals.dueChf }
      : null
    : carBillable && deposit > 0
      ? prepaymentPreview(deposit, draft.total)
      : null;
  const toPay = carFromDeal
    ? (carTotals?.dueChf ?? null)
    : prepayment
      ? prepayment.toPayChf
      : draft.total;

  const mutation = useMutation({
    mutationFn: () =>
      createInvoice(
        carFromDeal && prefill
          ? {
              userId,
              kind: 'car',
              dealId: prefill.dealId,
              dueAt: dueDate(dueDays).toISOString(),
            }
          : {
              userId,
              kind: activeKind,
              dueAt: dueDate(dueDays).toISOString(),
              lines: draft.lines,
            },
      ),
    onSuccess: (invoice) => {
      setIssued(invoice);
      // The balances table and the summary both change the moment an invoice
      // exists, and neither is this drawer's to re-fetch by hand.
      void queryClient.invalidateQueries({ queryKey: FINANCE_CLIENTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'finance', 'summary'] });
    },
  });

  const canSubmit =
    (carFromDeal && Boolean(userId) && Boolean(carTotals) && !mutation.isPending) ||
    (Boolean(client) &&
      activeKind !== 'car' &&
      draft.total > 0 &&
      (activeKind !== 'other' || descriptionValue.trim().length > 0) &&
      !mutation.isPending);

  const reset = () => {
    setKind(prefill ? 'car' : null);
    setOverrides({});
    setDescription(null);
    setDueDays(7);
    mutation.reset();
  };

  /**
   * The refusal, as an EXPLANATION.
   *
   * `PAYMENTS_DISABLED` is a policy state, not a fault: nothing is broken and
   * retrying will not help. It gets its own sentence and its own (info, not
   * danger) colour. Every other typed code keeps the API's own message — the
   * server said what was wrong and a generic box would throw that away.
   */
  const errorCode = financeErrorCode(mutation.error);
  const refusal = mutation.error
    ? {
        disabled: errorCode === 'PAYMENTS_DISABLED',
        // Unchanged for the issue: only PAYMENTS_DISABLED is re-worded; every
        // other code keeps the server's own sentence (it names the deal/line).
        text:
          errorCode === 'PAYMENTS_DISABLED'
            ? t('adminFinance.drawer.paymentsDisabled')
            : (financeErrorMessage(mutation.error) ?? t('adminFinance.drawer.submitFailed')),
      }
    : null;

  /** The rows step 03 renders: the server's fixed lines for a deal, else the draft. */
  const carLines: InvoiceLine[] | null = carFromDeal ? (carPreview?.lines ?? null) : null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-cf-ink/35 backdrop-blur-[2px]"
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal
        aria-label={t('adminFinance.drawer.title')}
        className="fixed inset-y-0 right-0 z-[61] flex w-[min(600px,100vw)] flex-col border-l border-cf-line bg-cf-ground leading-[normal] shadow-[-24px_0_60px_rgba(0,0,0,0.16)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-cf-line-soft bg-cf-surface px-6 pt-5 pb-4 shadow-[inset_0_3px_0_var(--color-cf-accent)]">
          <div>
            <p className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
              {issued
                ? t('adminFinance.drawer.eyebrowIssued', { no: issued.no })
                : t('adminFinance.drawer.eyebrowDraft')}
            </p>
            <h2 className="mt-1 text-[22px] font-semibold tracking-[-0.02em]">
              {t('adminFinance.drawer.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('adminFinance.drawer.close')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cf-fill text-[18px]"
          >
            ×
          </button>
        </div>

        {issued ? (
          <>
            <div className="flex flex-1 flex-col gap-4 overflow-auto px-6 py-7">
              <section className="flex flex-col gap-3.5 rounded-cf-lg border border-cf-line bg-cf-surface p-6 shadow-[inset_0_3px_0_var(--color-cf-success)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cf-success-tint text-[22px] text-cf-success">
                  ✓
                </span>
                <div>
                  <h3 className="text-[20px] font-semibold tracking-[-0.02em]">
                    {t('adminFinance.drawer.successTitle', { no: issued.no })}
                  </h3>
                  <p className="mt-1.5 text-[14px] leading-[1.5] text-cf-ink-muted">
                    {t('adminFinance.drawer.successBody')}
                  </p>
                </div>
                {/*
                  Every money figure here is the SERVER's answer (`issued`),
                  not the draft: the prepayment was computed in the issuing
                  transaction, and what is left to pay is the contract's
                  total − prepayment − paid.
                */}
                <div className="border-t border-cf-line-soft">
                  {(
                    [
                      [t('adminFinance.drawer.successClient'), client?.name ?? prefill?.clientName ?? userId],
                      [
                        t('adminFinance.drawer.successWhat'),
                        t(`adminFinance.invoiceKind.${issued.kind}`),
                      ],
                      [t('adminFinance.drawer.totalLines'), formatChf(issued.totalChf)],
                      ...(issued.prepaymentChf > 0
                        ? [
                            [
                              t('adminFinance.drawer.successPrepayment'),
                              `−${formatChf(issued.prepaymentChf)}`,
                            ],
                          ]
                        : []),
                      [t('adminFinance.drawer.successRemaining'), formatChf(invoiceOwed(issued))],
                      [
                        t('adminFinance.drawer.successRevenue'),
                        // The server's issued lines, for every kind — a car
                        // invoice for a deal has no draft lines to sum.
                        formatChf(
                          issued.lines
                            .filter((line) => line.ours)
                            .reduce((sum, line) => sum + line.valueChf, 0),
                        ),
                      ],
                    ] as Array<[string, string]>
                  ).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between gap-3 border-b border-cf-line-soft py-[9px] text-[13px]"
                    >
                      <span className="text-cf-ink-subtle">{key}</span>
                      <span className="text-right font-mono">{value}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
            <div className="flex gap-2 border-t border-cf-line bg-cf-surface px-6 pt-3.5 pb-[18px]">
              <button
                type="button"
                onClick={() => {
                  setIssued(null);
                  reset();
                }}
                className="h-[46px] rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[14px] font-semibold"
              >
                {t('adminFinance.drawer.again')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-[46px] flex-1 rounded-cf-md bg-cf-brand text-[14px] font-semibold text-white"
              >
                {t('adminFinance.drawer.done')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-1 flex-col gap-3.5 overflow-auto px-6 py-5">
              {/* ── 01 · Client ───────────────────────────────────────── */}
              <Step label={t('adminFinance.drawer.step1')}>
                <select
                  value={userId}
                  disabled={Boolean(prefill)}
                  onChange={(event) => {
                    setUserId(event.target.value);
                    reset();
                  }}
                  aria-label={t('adminFinance.drawer.clientLabel')}
                  className="h-11 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[15px] font-semibold text-cf-ink"
                >
                  {prefill && !client && (
                    <option value={prefill.userId}>{prefill.clientName || prefill.userId}</option>
                  )}
                  {clients.map((c) => (
                    <option key={c.userId} value={c.userId}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-1.5">
                  <Fact label={t('adminFinance.drawer.factType')} value={tierLabel(tier)} />
                  <Fact
                    label={t('adminFinance.drawer.factDeposit')}
                    value={deposit ? formatChf(deposit) : t('adminFinance.drawer.factNone')}
                    tone={deposit ? 'text-cf-info-ink' : 'text-cf-ink-subtle'}
                  />
                  <Fact
                    label={t('adminFinance.drawer.factDebt')}
                    value={owedAmount ? formatChf(owedAmount) : t('adminFinance.drawer.factNone')}
                    tone={owedAmount ? 'text-cf-warn' : 'text-cf-success'}
                  />
                </div>
                {/*
                  The client's own invoice list is NOT shown beside these
                  figures, and that is deliberate. `GET .../invoices?userId=`
                  answers the STORED collection while `outstandingChf` above
                  comes from the rollup, which also folds in provisional rows
                  derived from invoiced deals. Two readers of externally-written
                  documents can legitimately disagree, and presenting them side
                  by side as one reconciled set is how the drawer comes to
                  under-report a debt the table has right.
                */}
              </Step>

              {/* ── 02 · What for ─────────────────────────────────────── */}
              <Step label={t('adminFinance.drawer.step2')} gap="gap-3">
                <div className="flex flex-wrap gap-1.5">
                  {kinds.map((k) => (
                    <Chip
                      key={k}
                      active={k === activeKind}
                      onClick={() => {
                        setKind(k);
                        setOverrides({});
                        setDescription(null);
                        mutation.reset();
                      }}
                    >
                      {t(`adminFinance.drawer.kind.${k}`)}
                    </Chip>
                  ))}
                </div>

                {activeKind === 'car' && (
                  <p className="rounded-cf-md border border-cf-warn bg-cf-warn-tint px-3.5 py-3 text-[13px] leading-[1.5] text-cf-ink">
                    {carFromDeal
                      ? t('adminFinance.drawer.carFromDeal')
                      : t('adminFinance.drawer.carUnavailable')}
                  </p>
                )}

                {(activeKind === 'extra' || activeKind === 'other') && (
                  <input
                    value={descriptionValue}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder={t('adminFinance.drawer.descPlaceholder')}
                    aria-label={t('adminFinance.drawer.descLabel')}
                    className="h-11 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px] text-cf-ink"
                  />
                )}
              </Step>

              {/* ── 03 · Line items ───────────────────────────────────── */}
              <Step label={t('adminFinance.drawer.step3')} gap="gap-1">
                {carFromDeal ? (
                  previewQuery.isError ? (
                    <p
                      role="alert"
                      className="rounded-cf-md border border-cf-danger bg-cf-danger-tint px-3.5 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
                    >
                      {refusalText(previewQuery.error, 'adminFinance.drawer.refusal.previewFailed')}
                    </p>
                  ) : carLines === null ? (
                    <p className="py-2 text-[13px] leading-[1.5] text-cf-ink-subtle" aria-busy="true">
                      {t('adminFinance.drawer.previewLoading')}
                    </p>
                  ) : (
                    <>
                      {/* Fixed, system-calculated lines — no inputs, no corrections. */}
                      <InvoiceLinesReadOnly lines={carLines} />
                      <p className="mt-1 font-mono text-[11px] text-cf-ink-subtle">
                        {t('adminFinance.drawer.tariffVersion', { version: rules.customs.version })}
                      </p>
                    </>
                  )
                ) : draft.lines.length === 0 ? (
                  <p className="py-2 text-[13px] leading-[1.5] text-cf-ink-subtle">
                    {t('adminFinance.drawer.noLines')}
                  </p>
                ) : (
                  draft.lines.map((line) => (
                    <div
                      key={line.key}
                      className="grid grid-cols-[minmax(0,1fr)_130px] items-center gap-3 border-b border-cf-line-soft py-[7px]"
                    >
                      <InvoiceLineCaption line={line} />
                      <div
                        className={cn(
                          'flex h-[38px] items-center rounded-cf-md border bg-cf-surface px-2.5',
                          overrides[line.key] !== undefined ? 'border-cf-accent' : 'border-cf-line',
                        )}
                      >
                        <span className="font-mono text-[11px] text-cf-ink-subtle">CHF</span>
                        <input
                          value={formatNumber(line.valueChf)}
                          inputMode="numeric"
                          aria-label={line.label ?? t(line.labelKey)}
                          onChange={(event) => {
                            const digits = event.target.value.replace(/\D/g, '');
                            setOverrides((current) => ({
                              ...current,
                              [line.key]: digits ? Number.parseInt(digits, 10) : 0,
                            }));
                          }}
                          className="min-w-0 flex-1 border-0 bg-transparent px-0.5 text-right font-mono text-[14px] text-cf-ink outline-none"
                        />
                      </div>
                    </div>
                  ))
                )}
                {draft.lines.some(
                  (line) => line.basisKey === 'adminFinance.drawer.basis.perDeal',
                ) && (
                  <p className="mt-2 text-[12px] leading-[1.5] text-cf-ink-subtle">
                    {t('adminFinance.drawer.perDealNote')}
                  </p>
                )}
                {draft.lines.length > 0 && (
                  <p className="mt-1 font-mono text-[11px] text-cf-ink-subtle">
                    {t('adminFinance.drawer.tariffVersion', { version: rules.customs.version })}
                  </p>
                )}
              </Step>

              {/* ── 04 · Payment ──────────────────────────────────────── */}
              <Step label={t('adminFinance.drawer.step4')} gap="gap-3.5">
                {prepayment ? (
                  /*
                   * The artboard's «Зарахувати депозит» checkbox — rendered
                   * CHECKED and DISABLED. The server applies the prepayment to
                   * every car invoice in the issuing transaction and accepts no
                   * switch to skip it, so an interactive toggle would promise
                   * something the API cannot do.
                   */
                  <div
                    role="checkbox"
                    aria-checked="true"
                    aria-disabled="true"
                    aria-describedby="invoice-deposit-hint"
                    className="flex w-full items-center gap-3 text-left"
                  >
                    <span
                      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-[1.5px] border-cf-brand bg-cf-brand text-[12px] text-white"
                      aria-hidden
                    >
                      ✓
                    </span>
                    <span className="flex-1 text-[14px]">
                      {t('adminFinance.drawer.depositToggle', {
                        amount: formatChf(prepayment.prepaymentChf),
                      })}
                      <br />
                      <span id="invoice-deposit-hint" className="text-[12px] text-cf-ink-subtle">
                        {t('adminFinance.drawer.depositToggleHint')}
                      </span>
                    </span>
                  </div>
                ) : (
                  deposit > 0 &&
                  activeKind !== 'car' && (
                    <p className="text-[12px] leading-[1.5] text-cf-ink-subtle">
                      {t('adminFinance.drawer.depositHeld', { amount: chfOrDash(deposit) })}
                    </p>
                  )
                )}

                <div className="flex flex-col gap-2">
                  <span className="text-[13px] text-cf-ink-muted">
                    {t('adminFinance.drawer.dueOne')}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {INVOICE_DUE_DAYS.map((days) => (
                      <Chip key={days} active={dueDays === days} onClick={() => setDueDays(days)}>
                        {t(`adminFinance.drawer.days${days}`)}{' '}
                        <span className="font-mono text-[11px] font-medium opacity-75">
                          {dueDateLabel(days)}
                        </span>
                      </Chip>
                    ))}
                  </div>
                </div>
              </Step>
            </div>

            {/* ── Totals + submit ─────────────────────────────────────── */}
            <div className="flex flex-col gap-2.5 border-t border-cf-line bg-cf-surface px-6 pt-3.5 pb-[18px]">
              {refusal && (
                <p
                  role="alert"
                  className={cn(
                    'rounded-cf-md border px-3.5 py-2.5 text-[13px] leading-[1.5]',
                    refusal.disabled
                      ? 'border-cf-info bg-cf-info-tint text-cf-info-ink'
                      : 'border-cf-danger bg-cf-danger-tint text-cf-danger',
                  )}
                >
                  {refusal.text}
                </p>
              )}
              <div className="flex flex-col gap-1">
                <div className="flex justify-between text-[13px] text-cf-ink-muted">
                  <span>
                    {carFromDeal ? t('adminFinance.drawer.carTotal') : t('adminFinance.drawer.totalLines')}
                  </span>
                  <span className="font-mono" data-total>
                    {carFromDeal
                      ? carTotals
                        ? formatChf(carTotals.totalChf)
                        : '—'
                      : formatChf(draft.total)}
                  </span>
                </div>
                {prepayment && (
                  <div className="flex justify-between gap-3 text-[13px] text-cf-info-ink">
                    <span>
                      {carFromDeal
                        ? t('adminFinance.drawer.carPrepayment')
                        : prepayment.upTo
                          ? t('adminFinance.drawer.prepaymentUpTo')
                          : t('adminFinance.drawer.prepayment')}
                    </span>
                    <span className="font-mono">−{formatChf(prepayment.prepaymentChf)}</span>
                  </div>
                )}
                <div className="mt-0.5 flex items-baseline justify-between border-t border-cf-line-soft pt-1.5">
                  <span className="text-[15px] font-semibold">
                    {t('adminFinance.drawer.toPay')}
                  </span>
                  <span className="font-mono text-[26px] font-medium" data-to-pay>
                    {toPay === null ? '—' : formatChf(toPay)}
                  </span>
                </div>
                <div className="flex justify-between gap-3 text-[12px] text-cf-ink-subtle">
                  <span>{planText}</span>
                  {!carFromDeal && (
                    <span className="whitespace-nowrap">
                      {t('adminFinance.drawer.including')}{' '}
                      <span className="font-mono text-cf-brand">{formatChf(draft.ours)}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="h-[46px] rounded-cf-md border border-cf-line bg-cf-surface px-4 text-[14px] font-semibold"
                >
                  {t('adminFinance.drawer.cancel')}
                </button>
                {/*
                  BUG-30: a PAYMENTS_DISABLED refusal is a hold, not a transient
                  error — the button goes disabled (and faded) with it, so the
                  primary style cannot read as «try again». Every other refusal
                  leaves it live: the operator can fix the input and resubmit.
                */}
                <button
                  type="button"
                  onClick={() => mutation.mutate()}
                  disabled={!canSubmit || refusal?.disabled === true}
                  className="h-[46px] flex-1 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white disabled:opacity-40"
                >
                  {mutation.isPending
                    ? t('adminFinance.drawer.submitting')
                    : t('adminFinance.drawer.submit')}
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

/** One "Тип · Роздріб" pill in the client summary. */
function Fact({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-cf-sm bg-cf-ground px-2.5 py-[5px] text-[12px] text-cf-ink-muted">
      {label} <span className={cn('font-mono', tone)}>{value}</span>
    </span>
  );
}
